import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Skull, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../lib/format';
import { getProductCardImage } from '../../lib/images';

const CartDrawer = () => {
  const { items, subtotal, count, isDrawerOpen, closeDrawer, removeItem, updateQuantity } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDrawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && closeDrawer();
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [isDrawerOpen, closeDrawer]);

  if (!isDrawerOpen) return null;

  const goToCheckout = () => {
    closeDrawer();
    navigate('/checkout');
  };

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" role="dialog" aria-modal="true" aria-label="Cart">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeDrawer}></div>
      <div className="relative w-full max-w-md h-full bg-white border-l-8 border-black flex flex-col shadow-2xl animate-slide-in-right">
        <div className="bg-yellow-400 p-6 border-b-4 border-black flex justify-between items-center">
          <h2 className="text-4xl font-black italic">YOUR LOOT</h2>
          <button
            onClick={closeDrawer}
            className="p-2 bg-black text-white hover:bg-red-600 transition-colors border-2 border-white"
            aria-label="Close cart"
          >
            <X size={28} />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto p-6 space-y-4 bg-[url('https://www.transparenttextures.com/patterns/graphy.png')]">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center opacity-50">
              <Skull size={64} className="mb-4" />
              <div className="font-black text-2xl uppercase">Cart is Empty</div>
              <div className="font-mono text-sm mt-2">Feed your soul.</div>
            </div>
          ) : (
            items.map((item) => {
              const img = getProductCardImage(item);
              return (
                <div
                  key={`${item._id || item.slug}-${item.size}`}
                  className="flex gap-3 border-4 border-black bg-white p-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                >
                  {img ? (
                    <img src={img} alt={item.name} className="w-20 h-20 object-cover border-2 border-black flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 bg-gray-200 border-2 border-black flex items-center justify-center text-3xl font-black flex-shrink-0">
                      {item.name?.charAt(0)}
                    </div>
                  )}
                  <div className="flex-grow min-w-0">
                    <h4 className="font-black text-sm uppercase leading-tight mb-1 truncate">{item.name}</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-xs bg-black text-white px-2 py-0.5 uppercase">
                        SIZE {item.size}
                      </span>
                      <span className="font-mono font-bold text-sm">{formatPrice(item.salePrice ?? item.price)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center border-2 border-black">
                        <button
                          onClick={() => updateQuantity(item._id || item.id, item.size, (item.quantity || 1) - 1)}
                          className="px-2 py-1 hover:bg-yellow-400"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-3 font-bold text-sm">{item.quantity || 1}</span>
                        <button
                          onClick={() => updateQuantity(item._id || item.id, item.size, (item.quantity || 1) + 1)}
                          className="px-2 py-1 hover:bg-yellow-400"
                          aria-label="Increase quantity"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item._id || item.id, item.size)}
                        className="text-red-600 font-bold hover:bg-red-100 px-2 py-1 text-xs"
                      >
                        REMOVE
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="bg-white border-t-4 border-black p-6 z-10">
          <div className="flex justify-between text-2xl font-black mb-4 italic">
            <span>SUBTOTAL ({count})</span>
            <span>{formatPrice(subtotal)}</span>
          </div>
          <div className="text-xs font-mono opacity-60 mb-4">Shipping + taxes calculated at checkout.</div>
          <div className="flex gap-3">
            <Link
              to="/cart"
              onClick={closeDrawer}
              className="flex-1 bg-white text-black border-4 border-black py-3 text-sm font-black text-center hover:bg-gray-100"
            >
              VIEW CART
            </Link>
            <button
              onClick={goToCheckout}
              disabled={items.length === 0}
              className="flex-[2] bg-black text-white py-3 font-black text-lg hover:bg-green-500 hover:text-black transition-colors border-4 border-transparent hover:border-black disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              <ShoppingBag size={20} /> CHECKOUT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
